import { errorMessages } from '@readcloud/common';
import { LoadingIcon } from '@readcloud/component-library';
import { ErrorType } from '@readcloud/data';
import {
  applicationActions,
  readerActions,
  useAppDispatch,
} from '@readcloud/state';
import { Button } from '@readcloud/ui';
import * as React from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { makePageLabelsByLocation } from '../../constants';
import { ConnectedEPUBViewer } from '../EPUBViewer/ConnectedEPUBViewer';
import ImageViewer from '../ImageViewer/ImageViewer';
import { MediaViewer } from '../MediaViewer/MediaViewer';
import PDFViewer from '../PDFViewer/PDFViewer';
import { usePDFToggleToolbarsListener } from '../../hooks/usePDFToggleToolbarsListener';

interface IDocumentViewerProps {
  documentData: {
    data: any;
    type: string;
    error: ErrorType;
    loading: boolean;
    progress: number;
  };
  containerRef: React.RefObject<HTMLDivElement>;
  onGoBack: () => void;
  onReload: () => void;
}

const DocumentViewer: React.FunctionComponent<IDocumentViewerProps> = (
  props
) => {
  const dispatch = useAppDispatch();

  const {
    setData,
    setLocation,
    setPrepareError,
    setType,
    setProgress,
    setPreparingDocumentData,
    setScale,
    extras,
  } = React.useContext(DocumentWithExtrasContext);

  usePDFToggleToolbarsListener();

  React.useEffect(() => {
    if (props.documentData?.data) {
      setData(props.documentData?.data);
    }
  }, [props.documentData?.data]);

  React.useEffect(() => {
    setPrepareError(props.documentData?.error);
  }, [props.documentData?.error]);

  React.useEffect(() => {
    setType(props.documentData?.type);
  }, [props.documentData?.type]);

  React.useEffect(() => {
    setProgress(props.documentData?.progress);
  }, [props.documentData?.progress]);

  React.useEffect(() => {
    setPreparingDocumentData(props?.documentData?.loading);
  }, [props?.documentData?.loading]);

  React.useEffect(() => {
    if (extras?.labels) {
      dispatch(
        readerActions.updateReaderData({
          pageLabels: extras?.labels,
          pageLabelsByLocation: makePageLabelsByLocation(extras?.labels),
          loadingPageLabels: false,
          numPages: extras?.labels.length,
        })
      );
    }
  }, [extras?.labels]);

  React.useEffect(() => {
    if (props.documentData.error) {
      dispatch(applicationActions.updateApplication({ toolbarsOpen: false }));
    }
  }, [props.documentData.error]);

  const errorComponent = React.useMemo(() => {
    return (
      <div className="rc-flex rc-h-full rc-w-full rc-justify-center rc-items-center rc-text-center readcloud-ui-reset readcloud-ui">
        <div className="rc-flex rc-flex-col rc-gap-2">
          <small>Error Loading Content</small>

          <span>
            {errorMessages[props.documentData?.error?.type] ??
              'Something went wrong'}
          </span>

          <Button variant="ghost" onClick={props.onReload}>
            Retry
          </Button>
          <Button onClick={props.onGoBack}>Back to safety</Button>
        </div>
      </div>
    );
  }, [props.documentData?.error?.type, props.onReload, props.onGoBack]);

  return (
    <>
      <ErrorBoundary fallback={errorComponent}>
        <div id="clickaway-listener" className="rc-flex rc-h-full rc-w-full">
          {props.documentData.error ? (
            <div className="rc-flex rc-h-full rc-w-full rc-justify-center rc-items-center rc-text-center">
              <div className="rc-flex rc-flex-col rc-gap-2">
                <small>Error Loading Content</small>

                <span>{errorMessages[props.documentData.error.type]}</span>

                <Button variant="ghost" onClick={props.onReload}>
                  Retry
                </Button>
                <Button onClick={props.onGoBack}>Back to safety</Button>
              </div>
            </div>
          ) : !props?.documentData?.loading && props.documentData?.type ? (
            props.documentData.type === 'PDF' ? (
              <PDFViewer documentData={props.documentData} />
            ) : props.documentData.type === 'EPUB' ? (
              <ConnectedEPUBViewer container={props.containerRef} />
            ) : props.documentData.type === 'PLAYER' ? (
              <MediaViewer onGoBack={props.onGoBack} />
            ) : props.documentData.type === 'IMAGE' ? (
              <ImageViewer url={props.documentData.data} />
            ) : (
              <div className="rc-flex rc-h-full rc-w-full rc-justify-center rc-items-center rc-text-center">
                <div className="rc-flex rc-flex-col rc-gap-2">
                  <small>Error Loading Content</small>

                  <span>Cannot load this content.</span>

                  <Button variant="ghost" onClick={props.onReload}>
                    Retry
                  </Button>
                  <Button onClick={props.onGoBack}>Back to safety</Button>
                </div>
              </div>
            )
          ) : (
            <div className="rc-fixed rc-inset-0 rc-flex rc-flex-col rc-items-center rc-justify-center rc-gap-4 rc-bg-white/40">
              <div className="rc-flex rc-flex-col rc-items-center rc-justify-center rc-gap-4">
                <LoadingIcon
                  loading
                  progress={props.documentData?.progress}
                  position={'relative'}
                />

                <Button variant="secondary" onClick={props.onGoBack}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </ErrorBoundary>
    </>
  );
};

export default DocumentViewer;
